import { Suspense, lazy, useMemo, useState, useLayoutEffect } from 'react';
import { Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { RoutePath, GuardedRoute, history, routeAccess } from 'src/router';

import store, { useSelector } from 'src/store';
import { authRoleSelector, isLoggedInSelector } from 'src/store/selectors/authSelector';

import usePreviousLocationInStore from 'src/hooks/usePreviousLocationInStore';

import { PageLoading } from '@itm/shared-frontend/lib/components';

import AuthProvider from 'src/hocs/AuthProvider';
import SyncFiltersWithURL from 'src/hocs/SyncFiltersWithURL';
import Layout from 'src/components/layout/Layout';
import BreadcrumbsTrail from 'src/components/BreadcrumbsTrail';

import LoginRedirectPage from 'src/pages/loginRedirect';
import PermissionDeniedPage from 'src/pages/permissionDenied';

import { Role } from 'src/types';

const ConfigurationRoutes = lazy(() => import(/* webpackChunkName: "configuration" */ 'src/pages/configuration'));
const DataManagementRoutes = lazy(() => import(/* webpackChunkName: "uploads" */ 'src/pages/dataManagement'));
const AnalyticsAndReportsRoutes = lazy(() => import(/* webpackChunkName: "reports" */ 'src/pages/analyticsAndReports'));
const ProductInfoPage = lazy(() => import(/* webpackChunkName: "info" */ 'src/pages/productInfo/ProductInfo'));

function RootRoutesFallback() {
  return (
    <div className="px-3">
      <div className="container">
        <PageLoading />
      </div>
    </div>
  );
}

function RootRoutes() {
  const isLoggedIn = useSelector(isLoggedInSelector);
  const userRole = useSelector(authRoleSelector);
  usePreviousLocationInStore();

  const redirectRoute = useMemo(() => {
    if (!isLoggedIn || !userRole) return RoutePath.loginRedirect;
    switch (userRole) {
      case Role.SuperAdmin:
      case Role.Support:
      case Role.Analyst:
      case Role.ClientAdmin:
      case Role.Manager:
        return RoutePath.configurationRoot;
      case Role.Viewer:
        return RoutePath.analyticsRoot;
      case Role.CustomerOperations:
        return RoutePath.dataManagementRoot;
      default:
        userRole satisfies never; // ts exhaustive check
        return RoutePath.dataManagementRoot;
    }
  }, [isLoggedIn, userRole]);

  return (
    <Layout>
      <Suspense fallback={<RootRoutesFallback />}>
        <BreadcrumbsTrail />

        <Routes>
          <Route index element={<Navigate to={redirectRoute} replace={true} />} />

          <Route path={RoutePath.loginRedirect} element={<LoginRedirectPage />} />
          <Route path={RoutePath.permissionDenied} element={<PermissionDeniedPage />} />

          <Route
            path={`${RoutePath.configurationRoot}/*`}
            element={
              <GuardedRoute
                meta={{
                  auth: true,
                  role: { allowList: routeAccess.configurationRoot },
                }}
              >
                <ConfigurationRoutes />
              </GuardedRoute>
            }
          />

          <Route
            path={`${RoutePath.dataManagementRoot}/*`}
            element={
              <GuardedRoute
                meta={{
                  auth: true,
                  role: { allowList: routeAccess.dataManagementRoot },
                }}
              >
                <DataManagementRoutes />
              </GuardedRoute>
            }
          />

          <Route
            path={`${RoutePath.analyticsRoot}/*`}
            element={
              <GuardedRoute
                meta={{
                  auth: true,
                  role: { allowList: routeAccess.analyticsRoot },
                }}
              >
                <AnalyticsAndReportsRoutes />
              </GuardedRoute>
            }
          />

          <Route
            path={RoutePath.productInfo}
            element={
              <GuardedRoute
                meta={{
                  auth: true,
                  role: { allowList: routeAccess.productInfo },
                }}
              >
                <ProductInfoPage />
              </GuardedRoute>
            }
          />

          <Route path="*" element={<Navigate to={RoutePath.root} />} />
        </Routes>
      </Suspense>
    </Layout>
  );
}

function RootRouter() {
  const [state, setState] = useState({
    action: history.action,
    location: history.location,
  });
  useLayoutEffect(() => history.listen(setState), []);
  return (
    <Router location={state.location} navigationType={state.action} navigator={history}>
      <Provider store={store}>
        <AuthProvider>
          <SyncFiltersWithURL>
            <RootRoutes />
          </SyncFiltersWithURL>
        </AuthProvider>
      </Provider>
    </Router>
  );
}

export default RootRouter;
